import React, { useState, useEffect } from 'react';
import {
  Mail, Send, Eye, RefreshCw, CheckCircle, AlertTriangle,
  Shield, Heart, Key, Trash, Volume, Clock, Ban, Users, Receipt,
  Crown, Settings, FileText, Globe, X, Loader2
} from 'lucide-react';
import axios from 'axios';

const API_BASE = 'https://api.lifeshare.app/api/v1';

const TEMPLATE_ICONS: Record<string, React.ReactNode> = {
  shield: <Shield className="w-4 h-4" />,
  heart: <Heart className="w-4 h-4" />,
  key: <Key className="w-4 h-4" />,
  trash: <Trash className="w-4 h-4" />,
  mail: <Mail className="w-4 h-4" />,
  crown: <Crown className="w-4 h-4" />,
  volume: <Volume className="w-4 h-4" />,
  clock: <Clock className="w-4 h-4" />,
  ban: <Ban className="w-4 h-4" />,
  users: <Users className="w-4 h-4" />,
  receipt: <Receipt className="w-4 h-4" />,
};

interface EmailStatus {
  provider: string;
  configured: boolean;
  api_key_preview: string;
  from_addresses: Record<string, string>;
  app_url: string;
  email_from: string;
  logo_url: string;
  supported_languages: Array<{ code: string; name: string; flag: string }>;
  total_templates: number;
}

interface Template {
  id: string;
  name: string;
  method: string;
  icon: string;
}

interface EmailLog {
  id: string;
  to: string[];
  subject: string;
  created_at: string;
  status: string;
}

const EmailManagement: React.FC = () => {
  const [status, setStatus] = useState<EmailStatus | null>(null);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [logs, setLogs] = useState<EmailLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [logsLoading, setLogsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Test email state
  const [testEmail, setTestEmail] = useState('mamarecord@proton.me');
  const [testTemplate, setTestTemplate] = useState('verification');
  const [testLang, setTestLang] = useState('tr');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<{ success: boolean; message: string } | null>(null);

  // Preview state
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState<'templates' | 'test' | 'logs'>('templates');

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [statusRes, templatesRes] = await Promise.all([
        axios.get(`${API_BASE}/admin/email/status`),
        axios.get(`${API_BASE}/admin/email/templates`),
      ]);
      setStatus(statusRes.data);
      setTemplates(templatesRes.data.templates || []);
    } catch (err) {
      setError('Veriler yüklenemedi');
    }
    setLoading(false);
  };

  const fetchLogs = async () => {
    setLogsLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/admin/email/logs`);
      setLogs(res.data.emails || []);
    } catch (err) {
      setError('Loglar yüklenemedi');
    }
    setLogsLoading(false);
  };

  const sendTest = async () => {
    setSending(true);
    setSendResult(null);
    try {
      const res = await axios.post(`${API_BASE}/admin/email/test`, {
        to_email: testEmail,
        template_id: testTemplate,
        language: testLang,
      });
      setSendResult(res.data);
    } catch (err: any) {
      setSendResult({ success: false, message: err?.response?.data?.detail || 'Gönderme hatası' });
    }
    setSending(false);
  };

  const previewTemplate = async (templateId: string) => {
    setPreviewLoading(true);
    setShowPreview(true);
    setPreviewHtml(null);
    try {
      const res = await axios.post(`${API_BASE}/admin/email/preview`, {
        template_id: templateId,
        language: testLang,
      });
      setPreviewHtml(res.data.html);
    } catch (err) {
      setPreviewHtml('<p style="color:red;padding:20px;">Önizleme yüklenemedi</p>');
    }
    setPreviewLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 border-2 border-purple-300 border-t-purple-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-gray-900">Email Yönetimi</h1>
          <p className="text-sm text-gray-500 mt-1">Resend API · Şablonlar · Test · Loglar</p>
        </div>
        <button onClick={fetchAll} className="p-2.5 bg-gray-100 text-gray-600 rounded-xl hover:bg-gray-200 transition-colors">
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      {error && (
        <div className="bg-orange-50 border border-orange-200 rounded-2xl p-4 text-sm flex items-start gap-2">
          <AlertTriangle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
          <p className="text-orange-700">{error}</p>
        </div>
      )}

      {/* Status Cards */}
      {status && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Mail className="w-4 h-4 text-purple-600" />
              <span className="text-xs font-medium text-gray-500">Sağlayıcı</span>
            </div>
            <p className="text-lg font-semibold text-gray-900">Resend API</p>
            <div className="mt-1 flex items-center gap-1.5">
              {status.configured ? (
                <>
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span className="text-xs text-green-600">Aktif</span>
                </>
              ) : (
                <>
                  <AlertTriangle className="w-4 h-4 text-red-500" />
                  <span className="text-xs text-red-600">Yapılandırılmamış</span>
                </>
              )}
            </div>
          </div>

          <div className="bg-white rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-4 h-4 text-purple-600" />
              <span className="text-xs font-medium text-gray-500">Şablonlar</span>
            </div>
            <p className="text-lg font-semibold text-gray-900">{status.total_templates}</p>
            <p className="text-xs text-gray-400 mt-1">email şablonu</p>
          </div>

          <div className="bg-white rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Globe className="w-4 h-4 text-purple-600" />
              <span className="text-xs font-medium text-gray-500">Diller</span>
            </div>
            <p className="text-lg font-semibold text-gray-900">{status.supported_languages.length}</p>
            <p className="text-xs text-gray-400 mt-1">dil desteği</p>
          </div>

          <div className="bg-white rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Settings className="w-4 h-4 text-purple-600" />
              <span className="text-xs font-medium text-gray-500">API Key</span>
            </div>
            <p className="text-sm font-mono text-gray-700">{status.api_key_preview || '—'}</p>
          </div>
        </div>
      )}

      {/* From Addresses */}
      {status && (
        <div className="bg-white rounded-2xl p-5">
          <div className="flex items-center gap-2 text-purple-600 mb-3">
            <Mail className="w-5 h-5" />
            <h2 className="font-semibold text-gray-900">Gönderen Adresleri</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {Object.entries(status.from_addresses).map(([type, addr]) => (
              <div key={type} className="bg-gray-50 rounded-xl px-3 py-2">
                <p className="text-xs text-gray-400 capitalize">{type}</p>
                <p className="text-sm font-mono text-gray-700">{addr}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
        <button
          onClick={() => setActiveTab('templates')}
          className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'templates' ? 'bg-white text-purple-600 shadow-sm' : 'text-gray-500'}`}
        >
          Şablonlar
        </button>
        <button
          onClick={() => setActiveTab('test')}
          className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'test' ? 'bg-white text-purple-600 shadow-sm' : 'text-gray-500'}`}
        >
          Test Gönder
        </button>
        <button
          onClick={() => { setActiveTab('logs'); fetchLogs(); }}
          className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'logs' ? 'bg-white text-purple-600 shadow-sm' : 'text-gray-500'}`}
        >
          Loglar
        </button>
      </div>

      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {templates.map((tpl) => (
            <div key={tpl.id} className="bg-white rounded-2xl p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-xl bg-purple-50 flex items-center justify-center text-purple-600">
                    {TEMPLATE_ICONS[tpl.icon] || <Mail className="w-4 h-4" />}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{tpl.name}</p>
                    <p className="text-xs text-gray-400 font-mono">{tpl.id}</p>
                  </div>
                </div>
              </div>
              <button
                onClick={() => previewTemplate(tpl.id)}
                className="w-full flex items-center justify-center gap-1.5 px-3 py-2 bg-gray-50 hover:bg-purple-50 text-gray-600 hover:text-purple-600 rounded-xl text-sm font-medium transition-colors"
              >
                <Eye className="w-4 h-4" />
                Önizle ({testLang.toUpperCase()})
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Test Tab */}
      {activeTab === 'test' && (
        <div className="bg-white rounded-2xl p-5 space-y-4">
          <div className="flex items-center gap-2 text-purple-600 mb-2">
            <Send className="w-5 h-5" />
            <h2 className="font-semibold text-gray-900">Test Email Gönder</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Alıcı Email</label>
              <input
                type="email"
                value={testEmail}
                onChange={e => setTestEmail(e.target.value)}
                className="w-full px-3 py-2 bg-gray-50 rounded-xl text-sm outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="test@example.com"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Şablon</label>
              <select
                value={testTemplate}
                onChange={e => setTestTemplate(e.target.value)}
                className="w-full px-3 py-2 bg-gray-50 rounded-xl text-sm outline-none focus:ring-2 focus:ring-purple-500"
              >
                {templates.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Dil</label>
              <select
                value={testLang}
                onChange={e => setTestLang(e.target.value)}
                className="w-full px-3 py-2 bg-gray-50 rounded-xl text-sm outline-none focus:ring-2 focus:ring-purple-500"
              >
                {status?.supported_languages.map(l => (
                  <option key={l.code} value={l.code}>{l.flag} {l.name}</option>
                ))}
              </select>
            </div>
          </div>

          <button
            onClick={sendTest}
            disabled={sending || !testEmail}
            className="px-5 py-2.5 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            {sending ? 'Gönderiliyor...' : 'Test Email Gönder'}
          </button>

          {sendResult && (
            <div className={`rounded-xl p-3 text-sm flex items-start gap-2 ${sendResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
              {sendResult.success ? <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" /> : <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />}
              <p>{sendResult.message}</p>
            </div>
          )}
        </div>
      )}

      {/* Logs Tab */}
      {activeTab === 'logs' && (
        <div className="bg-white rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 text-purple-600">
              <FileText className="w-5 h-5" />
              <h2 className="font-semibold text-gray-900">Email Logları</h2>
            </div>
            <button onClick={fetchLogs} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
              <RefreshCw className={`w-4 h-4 ${logsLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {logsLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 text-purple-600 animate-spin" />
            </div>
          ) : logs.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">Log bulunamadı</p>
          ) : (
            <div className="space-y-2">
              {logs.map((log, idx) => (
                <div key={log.id || idx} className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{log.subject || '(no subject)'}</p>
                    <p className="text-xs text-gray-400 truncate">→ {log.to?.join(', ') || ''}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {log.status === 'sent' || log.status === 'delivered' ? (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    ) : log.status === 'bounced' || log.status === 'failed' ? (
                      <AlertTriangle className="w-4 h-4 text-red-500" />
                    ) : (
                      <Clock className="w-4 h-4 text-gray-400" />
                    )}
                    <span className="text-xs text-gray-400">
                      {log.created_at ? new Date(log.created_at).toLocaleString('tr-TR') : ''}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowPreview(false)}>
          <div className="bg-white rounded-2xl w-full max-w-4xl h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <h3 className="font-semibold text-gray-900">Email Önizleme</h3>
              <button onClick={() => setShowPreview(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            <div className="flex-1 overflow-hidden">
              {previewLoading ? (
                <div className="flex justify-center items-center h-full">
                  <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
                </div>
              ) : (
                <iframe
                  srcDoc={previewHtml || ''}
                  className="w-full h-full border-0"
                  title="Email Preview"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailManagement;
